import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export function AlertingPage() {
  useDocumentMeta({ title: 'Alerting & Notifications: LogPulse Docs', description: 'Configure alert rules, notification channels, muting rules, and maintenance windows in LogPulse.' });

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'creating-rules', label: 'Creating Alert Rules' },
    { id: 'alert-conditions', label: 'Alert Conditions' },
    { id: 'condition-threshold', label: 'Threshold', level: 1 },
    { id: 'condition-anomaly', label: 'Anomaly Detection', level: 1 },
    { id: 'condition-pattern', label: 'Pattern Match', level: 1 },
    { id: 'condition-absence', label: 'Log Absence', level: 1 },
    { id: 'notification-channels', label: 'Notification Channels' },
    { id: 'channel-configuration', label: 'Channel Configuration' },
    { id: 'channel-email', label: 'Email', level: 1 },
    { id: 'channel-slack', label: 'Slack', level: 1 },
    { id: 'channel-pagerduty', label: 'PagerDuty', level: 1 },
    { id: 'channel-teams', label: 'Microsoft Teams (Coming Soon)', level: 1 },
    { id: 'channel-webhook', label: 'Generic Webhook', level: 1 },
    { id: 'escalation-policies', label: 'Escalation Policies' },
    { id: 'alert-lifecycle', label: 'Alert Lifecycle' },
    { id: 'muting', label: 'Muting & Maintenance' },
    { id: 'mute-rules', label: 'Muting Alert Rules', level: 1 },
    { id: 'maintenance-windows', label: 'Maintenance Windows', level: 1 },
    { id: 'alert-history', label: 'Alert History & Analytics' },
    { id: 'best-practices', label: 'Best Practices' },
    { id: 'api-reference', label: 'API Reference' },
    { id: 'api-services', label: 'Monitored Services', level: 1 },
    { id: 'api-anomalies', label: 'Anomalies', level: 1 },
    { id: 'api-channels', label: 'Response Channels', level: 1 },
    { id: 'api-dependencies', label: 'Service Dependencies', level: 1 },
    { id: 'deduplication', label: 'Alert Deduplication' },
    { id: 'correlation', label: 'Alert Correlation (Roadmap)' },
    { id: 'slo-burn', label: 'SLO Burn Rate Alerts (Planned)' },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="Alerting & Notifications">
          <P>
            LogPulse alerting monitors your log data in real time and notifies you when
            conditions are met. LogPulse provides two complementary alerting systems: query-based
            alerts (threshold counting, pattern matching, and log absence detection) and
            AI-powered anomaly detection that automatically learns service baselines and
            detects deviations using statistical analysis.
          </P>
          <P>
            Each alert rule defines a condition, an evaluation schedule, one or more notification
            channels, and an optional escalation policy. When a condition is met, LogPulse
            creates an alert event and dispatches notifications through the configured channels.
          </P>
          <Table
            headers={['Alert Type', 'Description', 'Example Use Case']}
            rows={[
              ['Threshold', 'Fires when a count or aggregation exceeds a numeric threshold within a time window.', 'More than 100 errors in 5 minutes'],
              ['Anomaly Detection', 'AI-powered detection that learns service baselines and fires when metrics deviate beyond expected ranges using z-score analysis.', 'Unusual spike in error rate compared to the learned baseline for this time of day and day of week'],
              ['Pattern Match', 'Fires when a log entry matches a specific regex pattern.', 'Stack trace containing OutOfMemoryError'],
              ['Absence', 'Fires when no logs matching a query are received within a specified time window.', 'No heartbeat logs from payment-service for 10 minutes'],
            ]}
          />
        </Section>

        <Section id="creating-rules" title="Creating Alert Rules">
          <P>
            To create an alert rule, navigate to Anomaly Detection in the left sidebar and
            click Create Rule. The rule builder walks you through the following steps:
          </P>
          <P>
            1. Name and description: Give the rule a descriptive name and optional description.
            These appear in notifications, so make them actionable.
          </P>
          <P>
            2. LPQL condition: Enter the query that defines what to monitor. The query runs on
            a schedule and its results are evaluated against the condition.
          </P>
          <P>
            3. Condition type: Select from Threshold, Pattern Match, or Absence.
            Configure the specific parameters for the selected type. For AI-powered anomaly
            detection, use the Anomaly Detection section in the sidebar instead.
          </P>
          <P>
            4. Evaluation schedule: Set how often the query runs (evaluation interval) and the
            time window for each evaluation. The interval should be shorter than or equal to
            the window.
          </P>
          <P>
            5. Severity: Choose from Critical, Warning, or Info. This determines the urgency
            of notifications and the escalation behavior.
          </P>
          <P>
            6. Notification channels: Select one or more channels to receive alerts. You can
            assign different channels per severity level.
          </P>
          <P>
            7. Click Save Rule to activate the alert.
          </P>
          <CodeBlock title="Example -- Threshold alert rule">
{`Name: High Error Rate - API Gateway
LPQL: source=api-gateway level=error | stats count as error_count
Condition: error_count > 50
Window: 5 minutes
Interval: 1 minute
Severity: Warning
Channels: #ops-alerts (Slack), on-call@company.com (Email)`}
          </CodeBlock>
        </Section>

        <Section id="alert-conditions" title="Alert Conditions">
          <SubSection id="condition-threshold" title="Threshold">
            <P>
              Threshold alerts fire when a numeric aggregation result exceeds (or falls below)
              a specified value. The aggregation is computed from the LPQL query results over the
              configured time window.
            </P>
            <CodeBlock title="Threshold condition examples">
{`# Error count exceeds 100 in 5 minutes
level=error | stats count as cnt | where cnt > 100

# Average response time exceeds 2 seconds
source=api-gateway | stats avg(attributes.response_time_ms) as avg_rt
  | where avg_rt > 2000

# Distinct error sources exceed 5
level=error | stats dc(source) as source_count
  | where source_count > 5`}
            </CodeBlock>
            <Table
              headers={['Parameter', 'Description', 'Example']}
              rows={[
                ['Operator', 'Comparison operator: >, >=, <, <=, ==, !=', '> 100'],
                ['Window', 'Time window for aggregation', '5m, 15m, 1h'],
                ['Interval', 'How often the condition is evaluated', '1m, 5m'],
                ['Consecutive', 'Number of consecutive breaches before firing', '1 (default), 3'],
              ]}
            />
          </SubSection>

          <SubSection id="condition-anomaly" title="Anomaly Detection">
            <P>
              Anomaly detection automatically learns baseline behavior for each monitored service
              using historical data segmented by day of week and hour of day. When a metric deviates
              beyond the expected range (calculated using z-score analysis), an anomaly is triggered.
              The sensitivity can be configured as low, medium, or high per service.
            </P>
            <P>
              Monitored metrics include: log_count, error_count, error_rate, warn_count, warn_rate,
              info_count, and info_rate. The detection engine requires at least 5 days of baseline
              data before it can reliably detect anomalies. During the learning period, the service
              status shows as "learning" with progress information.
            </P>
            <CodeBlock title="Anomaly detection configuration">
{`Service: api-gateway
Metrics: error_count, error_rate
Sensitivity: medium
Detection interval: 2m
Detection lookback: 5m
Urgent detection: enabled (error rate > 50% with min batch size 20)`}
            </CodeBlock>
          </SubSection>

          <SubSection id="condition-pattern" title="Pattern Match">
            <P>
              Pattern match alerts fire when a log entry matches a regular expression pattern.
              Unlike threshold alerts that aggregate over a window, pattern match alerts evaluate
              each log entry individually and fire on the first match.
            </P>
            <CodeBlock title="Pattern match examples">
{`# Match Java OutOfMemoryError stack traces
Pattern: OutOfMemoryError|java\\.lang\\.OutOfMemoryError

# Match failed SSH login attempts
Pattern: Failed password for .+ from \\d+\\.\\d+\\.\\d+\\.\\d+

# Match credit card number patterns (for PII detection)
Pattern: \\b\\d{4}[- ]?\\d{4}[- ]?\\d{4}[- ]?\\d{4}\\b`}
            </CodeBlock>
          </SubSection>

          <SubSection id="condition-absence" title="Log Absence">
            <P>
              Absence alerts fire when no logs matching a query are received within a specified
              time window. This is commonly used for heartbeat monitoring, health check
              verification, and detecting silent failures.
            </P>
            <CodeBlock title="Absence alert examples">
{`# No heartbeat from payment-service for 10 minutes
LPQL: source=payment-service message="heartbeat"
Absence window: 10 minutes

# No successful deployments in 24 hours (for CI/CD monitoring)
LPQL: source=deploy-service level=info message="deployment successful"
Absence window: 24 hours`}
            </CodeBlock>
            <Callout type="warning">
              Set absence windows longer than your service's expected log interval. A heartbeat
              that logs every 60 seconds should have an absence window of at least 3-5 minutes
              to avoid false positives from temporary network delays.
            </Callout>
          </SubSection>
        </Section>

        <Section id="notification-channels" title="Notification Channels">
          <P>
            LogPulse supports four notification channel types. Each channel can be used by
            multiple alert rules, and each rule can send to multiple channels.
          </P>

          <SubSection id="channel-email" title="Email">
            <P>
              Email notifications are sent via Microsoft Graph. Configure the email service with
              your Azure credentials (tenant ID, client ID, client secret, and sender email address).
            </P>
            <P>
              Email alerts include the rule name, severity, condition details, a summary of
              matching logs, and a direct link to the Log Explorer with the alert query pre-loaded.
              Custom email templates are supported using Mustache syntax or the v2 block-based
              template format.
            </P>
          </SubSection>

          <SubSection id="channel-slack" title="Slack">
            <P>
              Slack notifications are sent via incoming webhooks. Each channel configuration
              specifies a webhook URL, a target channel, and optional user or group mentions
              for escalation.
            </P>
            <P>
              Slack messages include rich formatting with severity-colored sidebars, collapsible
              log excerpts, and action buttons for acknowledging or muting the alert directly
              from Slack.
            </P>
          </SubSection>

          <SubSection id="channel-pagerduty" title="PagerDuty">
            <P>
              PagerDuty integration uses the Events API v2. Configure it with your integration
              key and a severity mapping that translates LogPulse severity levels to PagerDuty
              urgencies. LogPulse routes alerts to the current on-call engineer using PagerDuty's
              escalation policies, so alerts automatically reach the right person.
            </P>
            <Table
              headers={['LogPulse Severity', 'PagerDuty Severity', 'PagerDuty Urgency']}
              rows={[
                ['Critical', 'critical', 'High'],
                ['Warning', 'warning', 'High'],
                ['Info', 'info', 'Low'],
              ]}
            />
          </SubSection>

          <SubSection id="channel-teams" title="Microsoft Teams (Coming Soon)">
            <Callout type="info">
              Microsoft Teams integration is planned for a future release. In the meantime, you
              can use the Generic Webhook channel to send notifications to Microsoft Teams via
              an incoming webhook connector.
            </Callout>
          </SubSection>

          <SubSection id="channel-webhook" title="Generic Webhook">
            <P>
              The generic webhook channel sends a configurable HTTP POST request to any URL.
              You can customize the request headers and payload template using Handlebars-style
              placeholders for alert data.
            </P>
            <CodeBlock title="Custom webhook payload template">
{`{
  "alert_name": "{{rule.name}}",
  "severity": "{{alert.severity}}",
  "triggered_at": "{{alert.triggered_at}}",
  "condition": "{{rule.condition}}",
  "matching_count": {{alert.matching_count}},
  "dashboard_url": "{{alert.dashboard_url}}",
  "logs_sample": {{alert.logs_sample_json}}
}`}
            </CodeBlock>
          </SubSection>
        </Section>

        <Section id="channel-configuration" title="Channel Configuration">
          <P>
            The following table lists all configuration parameters for each notification channel.
          </P>
          <Table
            headers={['Channel', 'Parameter', 'Required', 'Description']}
            rows={[
              ['Email', 'emails', 'Yes', 'Array of recipient email addresses'],
              ['Slack', 'webhookUrl', 'Yes', 'Slack incoming webhook URL'],
              ['Slack', 'channel', 'No', 'Override the default webhook channel'],
              ['PagerDuty', 'integrationKey', 'Yes', 'PagerDuty Events API v2 integration key'],
              ['PagerDuty', 'serviceName', 'No', 'Service name for PagerDuty event grouping'],
              ['Webhook', 'url', 'Yes', 'Target URL for the HTTP POST request'],
              ['Webhook', 'headers', 'No', 'Custom HTTP headers (JSON object)'],
            ]}
          />
        </Section>

        <Section id="escalation-policies" title="Escalation Policies">
          <P>
            Escalation policies define a sequence of notification actions that execute if an
            alert is not acknowledged within a specified time. Each level in the escalation
            chain specifies a delay and one or more channels.
          </P>
          <CodeBlock title="Example escalation policy">
{`Policy: Critical Service Alert
  Level 1 (immediate):
    - Channel: #ops-alerts (Slack)
    - Channel: on-call-engineer@company.com (Email)

  Level 2 (after 15 minutes unacknowledged):
    - Channel: #ops-critical (Slack, mention @oncall-group)
    - Channel: PagerDuty (High urgency)

  Level 3 (after 30 minutes unacknowledged):
    - Channel: engineering-managers@company.com (Email)
    - Channel: PagerDuty (Critical, escalation to management policy)`}
          </CodeBlock>
          <P>
            Escalation stops as soon as the alert is acknowledged at any level. If the alert
            is resolved automatically (the condition clears), all pending escalations are
            cancelled and a resolution notification is sent to all channels that received
            the original alert.
          </P>
          <Callout type="info">
            Escalation policies can reference on-call rotation schedules from PagerDuty.
            Configure the PagerDuty integration in Settings to enable rotation-aware escalations.
          </Callout>
        </Section>

        <Section id="alert-lifecycle" title="Alert Lifecycle">
          <P>
            Each alert event moves through a defined set of states. Understanding these states
            helps you manage alerts effectively and configure appropriate automation.
          </P>
          <Table
            headers={['State', 'Description', 'Transitions To']}
            rows={[
              ['Active', 'The alert condition is currently met. Notifications have been dispatched.', 'Acknowledged, Resolved, Muted'],
              ['Acknowledged', 'A team member has acknowledged the alert. Escalation is paused.', 'Resolved, Active (if re-triggered)'],
              ['Resolved', 'The alert condition is no longer met, or it was manually resolved.', 'Active (if condition recurs)'],
              ['Muted', 'The alert is suppressed. No notifications are sent while muted.', 'Active (when mute expires)'],
            ]}
          />
          <P>
            Alerts can be acknowledged via the LogPulse dashboard, Slack action buttons,
            PagerDuty, or the API. The acknowledgment includes the user identity and timestamp
            for audit purposes.
          </P>
          <P>
            Auto-resolve is enabled by default for threshold alerts. When the condition clears
            (the metric returns below the threshold), the alert is automatically resolved and
            a resolution notification is sent. Anomalies are auto-resolved when metrics return
            to normal ranges. Pattern match and absence alerts must be resolved manually or
            via the API. You can also provide feedback on anomalies (true positive or false
            positive) to help improve detection accuracy.
          </P>
        </Section>

        <Section id="muting" title="Muting & Maintenance Windows">
          <SubSection id="mute-rules" title="Muting Alert Rules">
            <P>
              You can mute individual alert rules or entire notification channels. Muted rules
              continue to evaluate their conditions but do not dispatch notifications. This is
              useful during known maintenance periods or when investigating a known issue.
            </P>
            <P>
              To mute a rule, click the Mute button on the rule detail page and set the mute
              duration. Mutes can be set for 30 minutes, 1 hour, 4 hours, 24 hours, or a
              custom duration. The mute expires automatically after the specified time.
            </P>
          </SubSection>

          <SubSection id="maintenance-windows" title="Maintenance Windows">
            <P>
              Maintenance windows mute all alerts (or a filtered subset) during a scheduled
              period. They are useful for planned deployments, infrastructure upgrades, or
              recurring maintenance tasks.
            </P>
            <CodeBlock title="Maintenance window configuration">
{`Name: Weekly Database Maintenance
Schedule: Every Sunday 02:00 - 04:00 UTC
Scope: source=db-* (all database services)
Recurrence: Weekly
Mute behavior: Suppress notifications, continue evaluation
Post-window: Re-evaluate all rules, notify if conditions still met`}
            </CodeBlock>
            <P>
              Recurring maintenance windows can be configured on daily, weekly, or monthly
              schedules. One-time windows are also supported for ad-hoc maintenance events.
            </P>
          </SubSection>
        </Section>

        <Section id="alert-history" title="Alert History & Analytics">
          <P>
            The Alert History page shows all past alert events with their state transitions,
            notification delivery status, and acknowledgment details. Use it to review incident
            timelines and measure response performance.
          </P>
          <Table
            headers={['Metric', 'Description', 'Target']}
            rows={[
              ['MTTA (Mean Time to Acknowledge)', 'Average time from alert firing to first acknowledgment.', 'Under 5 minutes for Critical, under 15 minutes for Warning'],
              ['MTTR (Mean Time to Resolve)', 'Average time from alert firing to resolution.', 'Under 30 minutes for Critical, under 2 hours for Warning'],
              ['False Positive Rate', 'Percentage of alerts that were resolved without action (no actual issue).', 'Under 10%'],
              ['Alert Volume', 'Total number of alerts fired per day, week, or month.', 'Varies by environment; track trends rather than absolutes'],
              ['Notification Delivery Rate', 'Percentage of notifications successfully delivered across all channels.', 'Above 99.5%'],
            ]}
          />
          <P>
            Alert analytics are available on the Anomaly Detection overview page. Use the time
            range picker to analyze alert trends over days, weeks, or months. The analytics
            dashboard includes charts for alert volume by severity, MTTA/MTTR trends, top
            firing rules, and channel delivery performance.
          </P>
        </Section>

        <Section id="best-practices" title="Best Practices">
          <P>
            Follow these guidelines to build an effective and sustainable alerting strategy:
          </P>
          <P>
            Do not alert on everything. Focus on conditions that require human intervention.
            If an alert does not require someone to take action, it should be a dashboard
            metric or a logged event, not an alert.
          </P>
          <P>
            Use severity levels consistently. Reserve Critical for issues that impact customers
            or revenue. Use Warning for degraded performance or approaching thresholds. Use Info
            for awareness items that do not require immediate action.
          </P>
          <P>
            Test your notification channels. After configuring a new channel, send a test
            notification to verify delivery. Test regularly to catch expired webhooks, rotated
            credentials, or changed channel configurations.
          </P>
          <P>
            Review alerts regularly. Schedule a monthly review of all alert rules. Disable or
            tune rules with high false positive rates. Remove rules for decommissioned services.
            Adjust thresholds based on current traffic patterns.
          </P>
          <P>
            Use escalation policies for critical alerts. Ensure that critical alerts always have
            a path to a human who can act, even outside business hours.
          </P>
          <Callout type="tip">
            Start with a small set of high-value alerts and expand gradually. It is better to
            have five alerts that always require action than fifty alerts that are routinely
            ignored.
          </Callout>
        </Section>

        <Section id="api-reference" title="API Reference">
          <P>
            Alert rules, channels, and mute windows can be managed programmatically via the
            LogPulse API. All endpoints require an API key with full access scope.
          </P>

          <SubSection id="api-services" title="Monitored Services">
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/services', 'List all monitored services'],
                ['POST', '/api/v1/detect/services', 'Add a new service to monitoring'],
                ['PUT', '/api/v1/detect/services/:id', 'Update service detection settings'],
                ['POST', '/api/v1/detect/services/:id/activate', 'Activate monitoring for a service'],
                ['POST', '/api/v1/detect/services/:id/dismiss', 'Dismiss a service from monitoring'],
                ['GET', '/api/v1/detect/services/:id/baseline-status', 'Get baseline learning status'],
                ['GET', '/api/v1/detect/services/:id/metrics-history', 'Get metrics history for a service'],
                ['GET', '/api/v1/detect/services/health', 'Get health overview of all services'],
              ]}
            />
          </SubSection>

          <SubSection id="api-anomalies" title="Anomalies">
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/anomalies', 'List anomalies with filtering'],
                ['GET', '/api/v1/detect/anomalies/:id', 'Get a specific anomaly'],
                ['PUT', '/api/v1/detect/anomalies/:id/acknowledge', 'Acknowledge an anomaly'],
                ['PUT', '/api/v1/detect/anomalies/:id/dismiss', 'Dismiss an anomaly'],
                ['PUT', '/api/v1/detect/anomalies/:id/feedback', 'Submit feedback (true/false positive)'],
                ['POST', '/api/v1/detect/anomalies/:id/investigate', 'Trigger AI investigation for an anomaly'],
                ['GET', '/api/v1/detect/anomalies/timeline', 'Get anomaly timeline data'],
              ]}
            />
          </SubSection>

          <SubSection id="api-channels" title="Response Channels">
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/response-channels', 'List all response channels'],
                ['POST', '/api/v1/detect/response-channels', 'Create a new response channel'],
                ['PUT', '/api/v1/detect/response-channels/:id', 'Update a response channel'],
                ['DELETE', '/api/v1/detect/response-channels/:id', 'Delete a response channel'],
              ]}
            />
          </SubSection>

          <SubSection id="api-dependencies" title="Service Dependencies">
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/detect/dependencies', 'List service dependencies'],
                ['POST', '/api/v1/detect/dependencies', 'Create a service dependency'],
                ['DELETE', '/api/v1/detect/dependencies/:id', 'Delete a service dependency'],
                ['POST', '/api/v1/detect/dependencies/:id/confirm', 'Confirm a discovered dependency'],
              ]}
            />
          </SubSection>

          <CodeBlock title="Example -- Add a monitored service via API">
{`curl -X POST https://api.logpulse.io/api/v1/detect/services \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $LOGPULSE_API_KEY" \\
  -d '{
    "service": "api-gateway",
    "displayName": "API Gateway",
    "metrics": ["error_count", "error_rate"],
    "sensitivity": "medium",
    "detectionInterval": "2m",
    "detectionLookback": "5m",
    "urgentDetectionEnabled": true,
    "urgentErrorRateThreshold": 50,
    "urgentMinBatchSize": 20
  }'`}
          </CodeBlock>

          <CodeBlock title="Example -- Create a response channel via API">
{`curl -X POST https://api.logpulse.io/api/v1/detect/response-channels \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $LOGPULSE_API_KEY" \\
  -d '{
    "name": "Ops Slack Channel",
    "type": "slack",
    "config": {
      "webhookUrl": "https://hooks.slack.com/services/T.../B.../xxx"
    },
    "minSeverity": "high",
    "enabled": true
  }'`}
          </CodeBlock>
        </Section>

        <Section id="deduplication" title="Alert Deduplication">
          <P>
            LogPulse deduplicates alerts to prevent notification fatigue. When an alert rule is
            already in the Active state, subsequent condition matches do not create new alert
            events or trigger additional notifications (unless re-notification is configured).
          </P>
          <Table
            headers={['Behavior', 'Description']}
            rows={[
              ['Same-rule grouping', 'Multiple condition matches within the same evaluation window are grouped into a single alert event'],
              ['State-based dedup', 'If a rule is already Active, new matches extend the alert duration but do not fire new notifications'],
              ['Re-notification interval', 'Configurable per rule (e.g., re-notify every 30 minutes while still active). Default: no re-notification'],
              ['Resolved + re-fire', 'If an alert auto-resolves and the condition triggers again, a new alert event is created'],
            ]}
          />
          <Callout type="tip">
            Configure re-notification intervals for critical alerts to ensure they are not
            forgotten. For low-priority alerts, rely on the dashboard and alert history instead
            of repeated notifications.
          </Callout>
        </Section>

        <Section id="correlation" title="Alert Correlation (Roadmap)">
          <P>
            Alert correlation is a planned feature that will group related alerts from multiple
            rules into a single incident. This reduces noise when a root cause triggers cascading
            failures across multiple services.
          </P>
          <Table
            headers={['Capability', 'Status', 'Description']}
            rows={[
              ['Multi-rule incidents', 'Planned', 'Correlate alerts from different rules that fire within the same time window into one incident'],
              ['Service dependency suppression', 'Planned', 'If an upstream service is alerting, suppress downstream alerts automatically based on the service dependency graph'],
              ['Topology-aware grouping', 'Planned', 'Group alerts by Kubernetes namespace, deployment, or service mesh topology'],
            ]}
          />
          <Callout type="info">
            Alert correlation is on the roadmap. The service dependency graph
            can already be configured via the API (see Service Dependencies above) and will be
            used by the correlation engine.
          </Callout>
        </Section>

        <Section id="slo-burn" title="SLO Burn Rate Alerts (Planned)">
          <P>
            SLO burn rate alerts are a planned feature that will let you define Service Level
            Objectives and alert when the error budget is being consumed faster than expected.
            This approach catches reliability degradation before the SLO is breached, giving your
            team time to respond.
          </P>
          <P>
            The planned implementation uses a multi-window burn rate algorithm:
          </P>
          <Table
            headers={['Window', 'Burn Rate Threshold', 'Use Case']}
            rows={[
              ['5 minutes', '14.4x', 'Page-level: critical issue consuming budget rapidly'],
              ['30 minutes', '6x', 'Ticket-level: significant degradation that needs attention within hours'],
              ['6 hours', '1x', 'Notification: slow burn that may breach the SLO within the budget period'],
            ]}
          />
          <P>
            Example: if your SLO is 99.9% success rate over a 30-day window, a 14.4x burn rate
            means you would exhaust the entire monthly error budget in about 2 hours. A 6x burn
            rate would exhaust it in about 5 hours.
          </P>
          <Callout type="info">
            SLO burn rate alerts are on the roadmap. They will integrate with
            the existing notification channels and escalation policies.
          </Callout>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
